'use client';

import { GlobalsCookieNotice } from '@/tina/__generated__/types';
import { useEffect } from 'react';
import { CookieNotificationContext } from './context';
import { useConsentCookies } from './hooks/useConsentCookies';
import { useConsentData } from './hooks/useConsentData';
import { useGtmConsentStore } from './hooks/useGtmStore';
import { usePermissionState } from './hooks/usePermissionState';
import { useUiActions } from './hooks/useUIActions';
import { InputCookie } from './setCookiesAction';
import { ConsentInput, CookieNoticeConsentData } from './types';
import { shallowEqualConsent } from './utils';
export const CONSENT_KEYS = ['ad_storage', 'analytics_storage', 'functionality_storage', 'personalization_storage', 'security_storage', 'ad_user_data', 'ad_personalization'] as const;
interface CookiePopoverProps {
  children?: React.ReactNode;
  setCookies: (...cookies: InputCookie[]) => Promise<void>;
  data: GlobalsCookieNotice;
}
export function CookieConsentProvider({
  children,
  setCookies,
  data
}: CookiePopoverProps) {
  const {
    getConsentFromPermission,
    getPermissionFromConsent,
    getAcceptedConsent,
    getDeclinedConsent
  } = useConsentData(data.consent as CookieNoticeConsentData[]);
  const {
    consent: gtmConsent,
    updateGtmConsent,
    getSnapshotFromGtm
  } = useGtmConsentStore();
  const {
    setConsent: setCookieConsent,
    getCookieValue
  } = useConsentCookies({
    setCookies
  });
  const [uiState, dispatchUiState] = usePermissionState({
    ...data.consent?.reduce((acc, consent) => {
      if (!consent) return acc;
      return {
        ...acc,
        [consent.id!]: false
      };
    }, {})
  });
  const {
    popoverRef,
    cookieSettingsRef,
    openSettings,
    closeSettings,
    closePopover,
    openPopover
  } = useUiActions();

  // sync consent changes from gtm to cookie
  useEffect(() => {
    async function syncGtmToCookie() {
      if (gtmConsent) {
        await setCookieConsent(gtmConsent);
      }
    }
    syncGtmToCookie();
  }, [gtmConsent, setCookieConsent]);
  useEffect(() => {
    function init(maxAttempts: number, attempt: number = 1, timeout: number = 750) {
      if (attempt > maxAttempts) {
        console.warn('GTM not ready after 20 attempts');
        return;
      }
      if (window._gtmIsReady) {
        const cookieConsent = getCookieValue();
        if (cookieConsent) {
          updateGtmConsent(cookieConsent);
          const permissions = getPermissionFromConsent(cookieConsent);
          if (!shallowEqualConsent(permissions, uiState)) {
            dispatchUiState({
              type: 'set_permissions',
              payload: {
                permissions
              }
            });
          }
          return;
        }
        const gtmConsent = getSnapshotFromGtm();
        if (gtmConsent) {
          const permissions = getPermissionFromConsent(gtmConsent);
          if (!shallowEqualConsent(permissions, uiState)) {
            dispatchUiState({
              type: 'set_permissions',
              payload: {
                permissions
              }
            });
          }
          if (!Object.values(permissions).every(value => value)) {
            openPopover();
          }
          return;
        }
      }
      setTimeout(() => init(maxAttempts, attempt + 1, timeout * 1.5), timeout);
    }
    init(20);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function acceptAllConsent() {
    dispatchUiState({
      type: 'accept_all_permissions'
    });
    const acceptedConsent = getAcceptedConsent();
    updateGtmConsent(acceptedConsent);
  }
  async function declineAllConsent() {
    dispatchUiState({
      type: 'reject_all_permissions'
    });
    const declinedConsent = getDeclinedConsent();
    updateGtmConsent(declinedConsent);
  }
  async function updateConsent(id: string, value: boolean) {
    dispatchUiState({
      type: 'set_permission',
      payload: {
        id,
        value
      }
    });
    const newConsents = getConsentFromPermission(id);
    if (!newConsents) return;
    const consent = Object.fromEntries(newConsents.map(key => [key, value ? 'granted' : 'denied'])) as ConsentInput;
    updateGtmConsent(consent);
  }
  return <CookieNotificationContext.Provider value={{
    closePopover,
    openSettings,
    closeSettings,
    acceptAllConsent,
    declineAllConsent,
    updateConsent,
    permissionState: uiState,
    popoverRef,
    settingsRef: cookieSettingsRef
  }}>
      {children}
    </CookieNotificationContext.Provider>;
}